import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Check, Loader2, AlertCircle, ShieldCheck, ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import { FALLBACK_PLANS, formatPrice, normalizePlan, isPopular, SERVICE_DISCLAIMER } from '../data/plans';
import { startSubscription, getSubscriptionPlanList, getSubscription, getCandidateProfile,
  checkPaymentInfo, updateCandidate, normalizePhone } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './PurchaseModal.css';

const PurchaseModal = ({ isOpen, onClose, currentPlanId = null, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState('plan'); // 'plan' | 'payment' | 'form'
  const iyzicoRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Dijital Temsil ve Otomasyon Açık Rıza Onayı + hizmetin SaaS lisansı olduğu onayı —
  // ikisi de zorunlu, ödeme adımına geçmeden önce ayrı ayrı onaylanmalı
  const [representationAccepted, setRepresentationAccepted] = useState(false);
  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // API'nin döndürdüğü ham mesaj — kullanıcıya ayrıntı olarak gösterilir
  const [errorDetail, setErrorDetail] = useState(null);
  const [formContent, setFormContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  // Adayın bilinen iletişim bilgileri — ilk çağrıda otomatik gönderilir
  const [contact, setContact] = useState({ phone: '', name: '', surname: '', email: '' });
  // Yalnızca gerçekten eksik (veya BE'nin reddettiği) alanlar sorulur
  const [askFields, setAskFields] = useState([]);
  const retryRef = useRef(0);

  // Sayfa arkasının kaymasını engelle
  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Modal kapanınca durumu sıfırla
  useEffect(() => {
    if (isOpen) {
      setStep('plan');
      setSelectedId(null);
      setTermsAccepted(false);
      setRepresentationAccepted(false);
      setLicenseAccepted(false);
      setError(null);
      setSubmitting(false);
      setFormContent(null);
      setAskFields([]);
      retryRef.current = 0;
      setContact({
        phone: user?.phone || '',
        name: user?.name || '',
        surname: user?.surname || '',
        email: user?.email || '',
      });
    }
  }, [isOpen, user?.name, user?.surname, user?.email, user?.phone]);

  // Telefon localStorage profilinde yok; adaydan çekip ön dolduruyoruz ki
  // kullanıcıya zaten bildiğimiz bilgiler sorulmasın
  useEffect(() => {
    if (!isOpen || !user?.candidateId) return;
    getCandidateProfile()
      .then((data) => {
        const c = data || {};
        setContact((prev) => ({
          phone: prev.phone || c.phone || '',
          name: prev.name || c.name || '',
          surname: prev.surname || c.surname || '',
          email: prev.email || c.email || '',
        }));
      })
      .catch(() => {});
  }, [isOpen, user?.candidateId]);

  // Paketleri backend'den çek; ulaşılamazsa yerel listeye düş
  useEffect(() => {
    if (!isOpen) return;
    setPlansLoading(true);
    getSubscriptionPlanList()
      .then((list) => {
        const normalized = (list || []).map(normalizePlan).sort((a, b) => a.sortingId - b.sortingId);
        setPlans(normalized.length ? normalized : FALLBACK_PLANS);
      })
      .catch(() => setPlans(FALLBACK_PLANS))
      .finally(() => setPlansLoading(false));
  }, [isOpen]);

  // iyzico'nun döndürdüğü içerik <script> barındırıyor; innerHTML ile eklenen
  // script'ler çalışmadığı için script düğümleri yeniden oluşturuluyor.
  // Kap DOM'a girdikten sonra çalışsın diye effect içinde yapılıyor.
  useEffect(() => {
    if (step !== 'form' || !formContent) return;
    const container = iyzicoRef.current;
    if (!container) {
      setError('failed');
      return;
    }
    container.innerHTML = '';

    // iyzico form'u bu id'li elemana render eder
    const mount = document.createElement('div');
    mount.id = 'iyzipay-checkout-form';
    mount.className = 'responsive';
    container.appendChild(mount);

    const holder = document.createElement('div');
    holder.innerHTML = formContent;
    Array.from(holder.childNodes).forEach((node) => {
      if (node.nodeName === 'SCRIPT') {
        const script = document.createElement('script');
        script.type = node.type || 'text/javascript';
        if (node.src) script.src = node.src;
        else script.text = node.textContent;
        container.appendChild(script);
      } else {
        container.appendChild(node.cloneNode(true));
      }
    });
  }, [step, formContent]);

  // iyzico ödeme sonrası FE'ye geri dönmüyor; sonucu backend'den yokluyoruz
  useEffect(() => {
    if (step !== 'form' || !selectedId) return undefined;

    const candidateId = user?.candidateId;
    if (!candidateId) return undefined;

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 75;   // 4sn × 75 ≈ 5 dakika
    const INTERVAL = 4000;

    const check = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const sub = await getSubscription(candidateId);
        const active = sub && ['ACTIVE', 'PENDING'].includes(String(sub.status).toUpperCase());
        if (active && sub.planId === selectedId) {
          if (cancelled) return;
          setStep('success');
          if (onSuccess) onSuccess(sub);
          return;
        }
      } catch {
        // Servis hazır değilse veya geçici hata varsa yoklamaya devam
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) timer = setTimeout(check, INTERVAL);
    };

    timer = setTimeout(check, INTERVAL);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [step, selectedId, user?.candidateId, onSuccess]);

  if (!isOpen) return null;

  const selected = plans.find((p) => p.id === selectedId) || null;
  // Paket id'leri backend GUID'i; verilmediyse ücretsiz paket geçerli sayılır
  const activePlanId = currentPlanId || plans.find((p) => p.isFree)?.id || null;

  const handleSelect = (planId) => {
    setSelectedId(planId);
    setStep('payment');
  };

  const beginPayment = async (overrides = {}) => {
    if (!selected) return;
    const info = { ...contact, ...overrides };
    setSubmitting(true);
    setError(null);
    setErrorDetail(null);

    try {
      // iyzico telefon ve ad/soyad olmadan checkout açmıyor — önce eksik var mı bak
      let missing = [];
      try {
        const check = await checkPaymentInfo();
        if (check.hasPhone === false) missing.push('phone');
        if (check.hasName === false) missing.push('name');
        if (check.hasSurname === false) missing.push('surname');
      } catch {
        // Kontrol ucu erişilemezse akışı durdurmuyoruz; StartSubscription hata verirse
        // aşağıdaki dallanma zaten eksik alanı soracak
      }

      if (missing.length) {
        // Kullanıcı formu doldurduysa profili güncelleyip devam et
        const filled = missing.every((f) => info[f]);
        if (!filled) {
          setAskFields(missing);
          setStep('contact');
          setSubmitting(false);
          return;
        }
        await updateCandidate({
          phone: info.phone ? normalizePhone(info.phone) : undefined,
          name: info.name || undefined,
          surname: info.surname || undefined,
        });
        setContact(info);
      }

      const data = await startSubscription({ planId: selected.id });
      if (data?.paymentPageUrl) {
        // iyzico'nun kendi ödeme sayfasına yönlendir
        window.location.href = data.paymentPageUrl;
      } else if (data?.checkoutFormContent) {
        // Formu modal içinde göster — asıl render useEffect'te, kap DOM'a
        // girdikten sonra yapılır
        setFormContent(data.checkoutFormContent);
        setStep('form');
        setSubmitting(false);
      } else {
        setError('failed');
        setSubmitting(false);
      }
    } catch (e) {
      setErrorDetail(e.message || null);
      switch (e.code) {
        case 'PHONE_REQUIRED':
        case 'EMAIL_REQUIRED': {
          const asked = e.code === 'PHONE_REQUIRED' ? 'phone' : 'email';
          const current = { ...contact, ...overrides };
          // Sorulan alan + gerçekten boş olan diğer alanlar
          const fields = [asked, 'phone', 'name', 'surname', 'email']
            .filter((f, i, arr) => arr.indexOf(f) === i)
            .filter((f) => f === asked || !current[f]);

          retryRef.current += 1;
          setAskFields(fields);
          setStep('contact');
          // Alan doluyken yine istendiyse değer geçersiz demektir
          if (current[asked] && retryRef.current > 1) setError('invalid');
          break;
        }
        case 'ALREADY_SUBSCRIBED':
          setError('already');
          break;
        case 'PLAN_NOT_FOUND':
          setError('plan');
          break;
        case 'NOT_IMPLEMENTED':
        case 'UNAUTHORIZED':
          setError('pending');
          break;
        default:
          setError('failed');
      }
      setSubmitting(false);
    }
  };

  const allConsentsAccepted = termsAccepted && representationAccepted && licenseAccepted;

  const handlePay = () => {
    if (!allConsentsAccepted || submitting) return;
    beginPayment();
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    beginPayment();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div className="purchase-overlay" onClick={handleOverlayClick}>
      <div className="purchase-modal" role="dialog" aria-modal="true" aria-label="Paket satın al">
        <button className="purchase-modal__close" onClick={onClose} aria-label="Kapat">
          <X size={18} />
        </button>

        {/* Adım göstergesi */}
        <div className="purchase-modal__steps">
          <span className={`purchase-modal__step ${step === 'plan' ? 'purchase-modal__step--active' : 'purchase-modal__step--done'}`}>
            1. Paket
          </span>
          <span className="purchase-modal__step-line" />
          <span className={`purchase-modal__step ${step === 'payment' ? 'purchase-modal__step--active' : ''}`}>
            2. Ödeme
          </span>
        </div>

        {step === 'plan' && (
          <>
            <h2 className="purchase-modal__title">AI Asistan Lisansını Seç</h2>
            <p className="purchase-modal__subtitle">
              Dilediğin zaman iptal edebilirsin. Abonelik aylık yenilenir. Kullanım paketleri
              yazılım lisans bedelidir; işe yerleştirme garantisi veya ücretli aracılık hizmeti
              içermez.
            </p>

            {plansLoading ? (
              <div className="purchase-plans-loading">
                <Loader2 size={20} className="purchase-spinner" /> Paketler yükleniyor...
              </div>
            ) : (
              <div className="purchase-modal__plans">
                {plans.map((plan) => {
                  const isCurrent = plan.id === activePlanId;
                  const popular = isPopular(plan, plans);
                  return (
                    <div
                      key={plan.id}
                      className={`purchase-plan ${popular ? 'purchase-plan--popular' : ''} ${isCurrent ? 'purchase-plan--current' : ''}`}
                    >
                      {popular && <span className="purchase-plan__badge">En çok tercih edilen</span>}
                      <div className="purchase-plan__head">
                        <h3 className="purchase-plan__name">{plan.name}</h3>
                        <div className="purchase-plan__price">
                          {formatPrice(plan.price, plan.currency)}
                          {!plan.isFree && plan.period && (
                            <span className="purchase-plan__period">/{plan.period}</span>
                          )}
                        </div>
                      </div>
                      <ul className="purchase-plan__features">
                        {plan.features.map((f, i) => (
                          <li key={i}><Check size={14} /> {f}</li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <span className="purchase-plan__current">Mevcut paketin</span>
                      ) : (
                        <button
                          className={`purchase-plan__btn ${popular ? 'purchase-plan__btn--primary' : ''}`}
                          onClick={() => handleSelect(plan.id)}
                          disabled={plan.isFree}
                        >
                          {plan.isFree ? 'Varsayılan paket' : 'Bu paketi seç'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {step === 'payment' && selected && (
          <>
            <button className="purchase-modal__back" onClick={() => setStep('plan')}>
              <ArrowLeft size={14} /> Paketler
            </button>

            <h2 className="purchase-modal__title">Ödeme</h2>
            <p className="purchase-modal__subtitle">Siparişini kontrol et ve ödemeye geç.</p>

            <div className="purchase-summary">
              <div className="purchase-summary__row">
                <span>Paket</span>
                <strong>{selected.name}</strong>
              </div>
              <div className="purchase-summary__row">
                <span>Kapsam</span>
                <strong>{selected.summary}</strong>
              </div>
              <div className="purchase-summary__row">
                <span>Abonelik dönemi</span>
                <strong>Aylık (otomatik yenilenir)</strong>
              </div>
              <div className="purchase-summary__row purchase-summary__row--total">
                <span>Toplam</span>
                <strong>
                  {formatPrice(selected.price, selected.currency)}
                  {selected.period && <span className="purchase-summary__period">/{selected.period}</span>}
                </strong>
              </div>
              <p className="purchase-summary__vat">Fiyatlara KDV dahildir.</p>
              <p className="purchase-summary__disclaimer">{SERVICE_DISCLAIMER}</p>
            </div>

            <label className="purchase-terms">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                <a href="/mesafeli-satis-sozlesmesi" target="_blank" rel="noopener noreferrer">Mesafeli Satış Sözleşmesi</a>
                {' ve '}
                <a href="/iptal-iade" target="_blank" rel="noopener noreferrer">İptal ve İade Koşulları</a>
                'nı okudum, onaylıyorum.
              </span>
            </label>

            <label className="purchase-terms">
              <input
                type="checkbox"
                checked={representationAccepted}
                onChange={(e) => setRepresentationAccepted(e.target.checked)}
              />
              <span>
                <a href="/kullanim-kosullari#dijital-temsil" target="_blank" rel="noopener noreferrer">Dijital Temsil ve Otomasyon Açık Rıza Onayı</a>
                'nı okudum. Bilgilerimin kamu/özel ilan platformlarında otomatik işlenmesi ve form
                doldurma süreçlerinde yazılıma dijital temsil yetkisi verdiğimi kabul ediyorum.
              </span>
            </label>

            <label className="purchase-terms">
              <input
                type="checkbox"
                checked={licenseAccepted}
                onChange={(e) => setLicenseAccepted(e.target.checked)}
              />
              <span>
                Bu hizmetin bir işe aracılık değil, kişisel veri işleme ve otomasyon yazılım
                lisansı olduğunu kabul ediyorum.
              </span>
            </label>

            {error && (
              <div className={`purchase-error ${error === 'pending' || error === 'already' ? 'purchase-error--pending' : ''}`}>
                <AlertCircle size={16} />
                <span>
                  {error === 'pending'
                    ? 'Ödeme altyapısı hazırlanıyor, çok yakında aktif olacak. Bu sırada paket satın alınamıyor.'
                    : error === 'already'
                      ? 'Zaten aktif bir aboneliğin var. Paketini Aboneliğim sayfasından yönetebilirsin.'
                      : error === 'plan'
                        ? 'Seçtiğin paket bulunamadı. Sayfayı yenileyip tekrar dener misin?'
                        : 'Ödeme başlatılamadı. Lütfen tekrar deneyin.'}
                  {errorDetail && <span className="purchase-error__detail">{errorDetail}</span>}
                </span>
              </div>
            )}

            <button
              className="purchase-pay-btn"
              onClick={handlePay}
              disabled={!allConsentsAccepted || submitting}
            >
              {submitting ? (
                <><Loader2 size={16} className="purchase-spinner" /> Yönlendiriliyor...</>
              ) : (
                <><Lock size={15} /> Ödemeye Geç</>
              )}
            </button>

            <div className="purchase-iyzico-badge">
              <img src="/iyzico-ile-ode.svg" alt="iyzico ile öde" width="120" height="28" />
            </div>

            <p className="purchase-secure">
              <ShieldCheck size={14} />
              Ödeme, iyzico'nun güvenli altyapısında 3D Secure ile tamamlanır. Kart
              bilgileriniz isBulsun.com sunucularında saklanmaz.
            </p>
          </>
        )}

        {step === 'contact' && selected && (
          <>
            <button className="purchase-modal__back" onClick={() => setStep('payment')}>
              <ArrowLeft size={14} /> Sipariş özeti
            </button>

            <h2 className="purchase-modal__title">
              {askFields.length === 1 && askFields[0] === 'phone' ? 'Telefon numaran' : 'Birkaç bilgi daha'}
            </h2>
            <p className="purchase-modal__subtitle">
              {error === 'invalid'
                ? 'Girdiğin bilgi kabul edilmedi, kontrol edip tekrar dener misin?'
                : 'Ödemeyi başlatabilmek için aşağıdaki bilgiler gerekiyor.'}
            </p>

            <form className="purchase-contact" onSubmit={handleContactSubmit}>
              {askFields.includes('phone') && (
                <label className="purchase-contact__field">
                  <span>Telefon</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="+90 5XX XXX XX XX"
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    required
                    autoFocus
                  />
                </label>
              )}

              {askFields.includes('email') && (
                <label className="purchase-contact__field">
                  <span>E-posta</span>
                  <input
                    type="email"
                    placeholder="ornek@eposta.com"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    required
                    autoFocus={!askFields.includes('phone')}
                  />
                </label>
              )}

              {(askFields.includes('name') || askFields.includes('surname')) && (
                <div className="purchase-contact__row">
                  {askFields.includes('name') && (
                    <label className="purchase-contact__field">
                      <span>Ad</span>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        required
                      />
                    </label>
                  )}
                  {askFields.includes('surname') && (
                    <label className="purchase-contact__field">
                      <span>Soyad</span>
                      <input
                        type="text"
                        value={contact.surname}
                        onChange={(e) => setContact({ ...contact, surname: e.target.value })}
                        required
                      />
                    </label>
                  )}
                </div>
              )}

              {error && error !== 'invalid' && (
                <div className={`purchase-error ${error === 'pending' || error === 'already' ? 'purchase-error--pending' : ''}`}>
                  <AlertCircle size={16} />
                  <span>
                    {error === 'already'
                      ? 'Zaten aktif bir aboneliğin var.'
                      : error === 'pending'
                        ? 'Ödeme altyapısı hazırlanıyor, çok yakında aktif olacak.'
                        : 'Ödeme başlatılamadı. Lütfen tekrar deneyin.'}
                    {errorDetail && <span className="purchase-error__detail">{errorDetail}</span>}
                  </span>
                </div>
              )}

              <button className="purchase-pay-btn" type="submit" disabled={submitting}>
                {submitting ? (
                  <><Loader2 size={16} className="purchase-spinner" /> Gönderiliyor...</>
                ) : (
                  <><Lock size={15} /> Ödemeye Devam Et</>
                )}
              </button>
            </form>
          </>
        )}

        {step === 'form' && selected && (
          <>
            <button className="purchase-modal__back" onClick={() => setStep('payment')}>
              <ArrowLeft size={14} /> Sipariş özeti
            </button>

            <h2 className="purchase-modal__title">Kart bilgileri</h2>
            <p className="purchase-modal__subtitle">
              {selected.name} · {formatPrice(selected.price, selected.currency)}/{selected.period}
            </p>

            <p className="purchase-poll-note">
              <Loader2 size={13} className="purchase-spinner" />
              Ödemeni tamamladıktan sonra bu pencere otomatik güncellenecek.
            </p>

            {/* iyzico checkout form buraya render eder */}
            <div className="purchase-iyzico" ref={iyzicoRef}>
              <div className="purchase-iyzico__loading">
                <Loader2 size={18} className="purchase-spinner" /> Güvenli ödeme formu yükleniyor...
              </div>
            </div>

            {error && (
              <div className="purchase-error">
                <AlertCircle size={16} />
                <span>
                  Ödeme formu yüklenemedi. Lütfen tekrar deneyin.
                  {errorDetail && <span className="purchase-error__detail">{errorDetail}</span>}
                </span>
              </div>
            )}

            <p className="purchase-secure">
              <ShieldCheck size={14} />
              Bu form iyzico tarafından sağlanır. Kart bilgileriniz isBulsun.com sunucularına
              hiçbir zaman gönderilmez.
            </p>
          </>
        )}

        {step === 'success' && selected && (
          <div className="purchase-success">
            <div className="purchase-success__icon">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="purchase-modal__title">Aboneliğin başladı</h2>
            <p className="purchase-modal__subtitle">
              <strong>{selected.name}</strong> paketi aktif edildi. Paketin sunduğu tüm
              özellikleri hemen kullanmaya başlayabilirsin.
            </p>
            <div className="purchase-success__actions">
              <a href="/panel/abonelik" className="purchase-success__btn purchase-success__btn--primary">
                Aboneliğimi Gör
              </a>
              <button className="purchase-success__btn" onClick={onClose}>Kapat</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PurchaseModal;
